import { useModal } from "@/Contexts";
import { useGlobalAlert } from "@/States/globalAlert";
import { getFileExtension } from "@/Utils/getFileExtension";
import { ALLOWED_FILE_TYPES, FILE_SIZE_LIMIT } from "@/constants/fileUpload";
import { AlertModal } from "@/pages/infrastructure/modals/alertModal";
import { useTrans } from "@/translations";

import { type ChangeEvent, useCallback, useRef, useState } from "react";

type UseLogoUploadOptions = {
  getUploadUrl: (
    filename: string,
  ) => Promise<{ url: string; oldFileName?: string }>;
  cleanUpLogoFiles: (oldFileName: string) => Promise<void>;
  deleteLogo: (fileName: string, options: { onSuccess: () => void }) => void;
  onComplete: () => void;
  currentFileName: string | undefined;
};

export const useLogoUpload = ({
  getUploadUrl,
  cleanUpLogoFiles,
  deleteLogo,
  onComplete,
  currentFileName,
}: UseLogoUploadOptions) => {
  const { t } = useTrans();
  const inputRef = useRef<HTMLInputElement>(null);
  const { showModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();
  const [isUploading, setIsUploading] = useState(false);

  const triggerFileUpload = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const clear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, []);

  const onAddFile = useCallback(
    async (file: File) => {
      if (file.size > FILE_SIZE_LIMIT) {
        showModal(
          <AlertModal
            title={t("errors:error")}
            hint={t("errors:fileTooLarge", {
              size: FILE_SIZE_LIMIT / (1024 * 1024),
            })}
          />,
        );
        return;
      }

      const fileExtension = getFileExtension(file.name);
      if (!ALLOWED_FILE_TYPES.includes(fileExtension)) {
        showModal(
          <AlertModal
            title={t("errors:error")}
            hint={t("errors:fileTypeNotAllowed", { fileType: fileExtension })}
          />,
        );
        return;
      }

      setIsUploading(true);

      try {
        const { url, oldFileName } = await getUploadUrl(file.name);

        const uploadResponse = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          setGlobalAlert({
            type: "error",
            message: "systemMessages:iconUploadFailed",
          });
          return;
        }

        setGlobalAlert({
          type: "success",
          message: "systemMessages:iconUploadedSuccessfully",
        });

        // Clean up old logo only after the new one is confirmed uploaded
        if (oldFileName) {
          try {
            await cleanUpLogoFiles(oldFileName);
          } catch {
            showModal(
              <AlertModal
                title={t("errors:error")}
                hint={t("errors:failedToCleanUpOldLogo")}
              />,
            );
          }
        }

        onComplete();
      } finally {
        setIsUploading(false);
      }
    },
    [getUploadUrl, cleanUpLogoFiles, onComplete, showModal, t, setGlobalAlert],
  );

  const onFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) {
        return;
      }

      try {
        await onAddFile(e.target.files[0]);
      } catch {
        setGlobalAlert({
          type: "error",
          message: "systemMessages:iconUploadFailed",
        });
      } finally {
        clear();
      }
    },
    [onAddFile, clear, setGlobalAlert],
  );

  const handleDelete = useCallback(() => {
    if (!currentFileName) return;
    deleteLogo(currentFileName, {
      onSuccess: onComplete,
    });
  }, [deleteLogo, currentFileName, onComplete]);

  return {
    inputRef,
    triggerFileUpload,
    onFileChange,
    onAddFile,
    handleDelete,
    hasLogo: !!currentFileName,
    isUploading,
  };
};
